import { useState } from 'react';
import { MESES_COBRADOS_EN_PLAN_ANUAL, type PlanPublico } from '@medchoice/shared';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import { useSesion } from '../../session/Sesion';
import { Boton, Cargando, FalloDeCarga, Insignia, Tarjeta } from '../../ui';
import { pesos } from '../../ui/format';
import { Comparacion } from './Comparacion';

const PLAN_DESTACADO = 'Profesional';
const MESES_DE_REGALO = 12 - MESES_COBRADOS_EN_PLAN_ANUAL;

/** "8 % en el plan Básico y 4 % en Profesional y Premium", armado con la comisión de cada plan. */
function comisiones(planes: PlanPublico[]): string {
  const porComision = new Map<number, string[]>();
  for (const p of planes) porComision.set(p.comision_sena, [...(porComision.get(p.comision_sena) ?? []), p.nombre]);
  const partes = [...porComision].map(([comision, nombres]) =>
    `${Math.round(comision * 100)} % en ${nombres.length === 1 ? `el plan ${nombres[0]}` : nombres.join(' y ')}`);
  return partes.join(' y ');
}


export function Planes() {
  const [anual, setAnual] = useState(false);
  const { usuario } = useSesion();
  const pedido = usePedido(() => publico.catalogos(), []);

  const elegir = () => irA(usuario?.rol === 'profesional' ? '/pro/cuenta' : '/registro-profesional');

  return (
    <div className="planes">
      <header className="planes__encabezado">
        <h1>Empezás gratis. Pagás cuando te sirve.</h1>
        <p>Todos los planes incluyen perfil verificado, agenda online y cobro de seña.</p>
        <div className="planes__periodo" role="group" aria-label="Período de facturación">
          <Boton variante={anual ? 'secundario' : 'primario'} tamano="chico" onClick={() => setAnual(false)}>Mensual</Boton>
          <Boton variante={anual ? 'primario' : 'secundario'} tamano="chico" onClick={() => setAnual(true)}>
            Anual <Insignia tono="coral" tamano="chico">{MESES_DE_REGALO} meses gratis</Insignia>
          </Boton>
        </div>
      </header>

      {pedido.estado === 'cargando' && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      <div className="planes__grilla">
        {pedido.datos?.planes.map((plan) => (
          <TarjetaDePlan key={plan.id_plan} plan={plan} anual={anual} alElegir={elegir} />
        ))}
      </div>
      <p className="planes__nota">
        {pedido.datos && `Sobre cada seña cobrada, MedChoice retiene una comisión: ${comisiones(pedido.datos.planes)}. `}
        El plan anual cobra {MESES_COBRADOS_EN_PLAN_ANUAL} meses y regala {MESES_DE_REGALO}.
      </p>

      {pedido.datos && <Comparacion planes={pedido.datos.planes} competencia={pedido.datos.competencia} />}
    </div>
  );
}

function TarjetaDePlan({ plan, anual, alElegir }: { plan: PlanPublico; anual: boolean; alElegir: () => void }) {
  const destacado = plan.nombre === PLAN_DESTACADO;
  const precio = anual ? plan.precio_anual : plan.precio_mensual;
  return (
    <Tarjeta>
      <div className={destacado ? 'plan plan--destacado' : 'plan'}>
        {destacado && <Insignia tono="nuevo">El más elegido</Insignia>}
        <h2>{plan.nombre}</h2>
        <p className="plan__precio">
          {precio === 0 ? 'Gratis' : pesos(precio)}
          {precio > 0 && <span>/{anual ? 'año' : 'mes'}</span>}
        </p>
        <p className="plan__descripcion">{plan.descripcion}</p>
        <Boton variante={destacado ? 'primario' : 'secundario'} anchoCompleto onClick={alElegir}>
          {plan.precio_mensual === 0 ? 'Empezar gratis' : `Elegir ${plan.nombre}`}
        </Boton>
      </div>
    </Tarjeta>
  );
}
